import "./screen-lock.css";
import { Button, Input } from "@rakazo/ui-web";
import { ArrowRight, LockKeyhole } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { discardPrimedBootstrap, peekInitialBootstrap } from "../lib/bootstrap";
import { appName } from "../lib/branding";
import { AppMark } from "./AppMark";
import { CodeField } from "./CodeField";

type Status = { enabled: boolean; locked: boolean };
type LockContext = { enabled: boolean; busy: boolean; lock: () => void };
const Context = createContext<LockContext>({ enabled: false, busy: false, lock: () => {} });

async function request(action: string, code?: string): Promise<Status> {
  const response = await fetch(`/api/screen-lock/${action}`, {
    credentials: "same-origin",
    cache: "no-store",
    ...(action === "status"
      ? {}
      : {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(code === undefined ? {} : { code }),
        }),
  });
  if (!response.ok) {
    throw new Error(
      action === "unlock" && response.status === 403
        ? "Could not unlock. Check your code or try again shortly."
        : "Could not reach the server. Try again.",
    );
  }
  const result = await response.json();
  return { enabled: result.enabled ?? true, locked: result.locked };
}

export function ScreenLockButton() {
  const { enabled, busy, lock } = useContext(Context);
  return enabled ? (
    <Button
      variant="ghost"
      size="icon-sm"
      className="app-no-drag"
      aria-label="Lock screen"
      title="Lock screen"
      disabled={busy}
      onClick={lock}
    >
      <LockKeyhole size={17} strokeWidth={1.6} />
    </Button>
  ) : null;
}

export function ScreenLockGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [connectionError, setConnectionError] = useState("");
  const [code, setCode] = useState("");
  const version = useRef(0);
  const channel = useRef<BroadcastChannel | null>(null);
  const refresh = useCallback(async () => {
    const current = ++version.current;
    try {
      const next = await request("status");
      if (current !== version.current) return;
      if (next.locked) {
        const primed = peekInitialBootstrap();
        if (primed) discardPrimedBootstrap(primed);
      }
      setStatus(next);
      setConnectionError("");
    } catch {
      if (current === version.current) setConnectionError("Could not reach the server. Try again.");
    }
  }, []);
  useEffect(() => {
    void refresh();
    const update = () => {
      void refresh();
    };
    if (typeof BroadcastChannel !== "undefined") {
      channel.current = new BroadcastChannel("workspace-screen-lock");
      channel.current.onmessage = (event) => {
        if (event.data === "locked") {
          ++version.current;
          setStatus({ enabled: true, locked: true });
          setCode("");
        } else update();
      };
    }
    window.addEventListener("focus", update);
    window.addEventListener("online", update);
    const timer = setInterval(update, 10_000);
    return () => {
      ++version.current;
      clearInterval(timer);
      window.removeEventListener("focus", update);
      window.removeEventListener("online", update);
      channel.current?.close();
    };
  }, [refresh]);
  const lock = useCallback(async () => {
    ++version.current;
    setBusy(true);
    setError("");
    try {
      await request("lock");
      ++version.current;
      setStatus({ enabled: true, locked: true });
      setCode("");
      channel.current?.postMessage("locked");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);
  async function unlock(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    ++version.current;
    setBusy(true);
    setError("");
    try {
      await request("unlock", code);
      ++version.current;
      setCode("");
      setStatus({ enabled: true, locked: false });
      channel.current?.postMessage("unlocked");
    } catch (cause) {
      setCode("");
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const locked = !status || status.locked;
  return (
    <Context.Provider
      value={{
        enabled: status?.enabled ?? false,
        busy,
        lock: () => {
          void lock();
        },
      }}
    >
      {!locked && children}
      {locked ? (
        <main
          className="app-canvas command-home lock-screen relative isolate flex h-full items-center justify-center overflow-hidden"
          aria-label="Screen locked"
        >
          <CodeField />
          <div className="app-drag absolute inset-x-0 top-0 h-12" />
          <div className="lock-screen-content relative z-10 flex w-full flex-col items-center px-6 text-center">
            <div className="lock-screen-mark">
              <AppMark className="lock-screen-head">
                <LockKeyhole className="size-24 text-muted-foreground" />
              </AppMark>
            </div>
            <h1 className="lock-screen-wordmark" aria-label={appName}>
              <span>{appName.split(" ")[0]}</span>
              <span>{appName.split(" ").slice(1).join(" ")}</span>
            </h1>
            {status?.locked ? (
              <form onSubmit={unlock} className="lock-screen-form">
                <div className="flex items-center gap-2">
                  <Input
                    autoFocus
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    aria-label="Unlock code"
                    placeholder="Enter code"
                    value={code}
                    maxLength={128}
                    onChange={(event) => setCode(event.target.value)}
                    className="h-11 rounded-xl bg-background/50 pl-4 text-sm focus-visible:border-foreground/40 focus-visible:ring-foreground/10"
                  />
                  <Button
                    type="submit"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Unlock"
                    disabled={busy || !code}
                    className="size-11 shrink-0 rounded-xl border border-border bg-foreground/5"
                  >
                    <ArrowRight size={17} />
                  </Button>
                </div>
              </form>
            ) : (
              <Button
                variant="ghost"
                className="mt-7"
                onClick={() => {
                  setError("");
                  void refresh();
                }}
              >
                {error || connectionError ? "Try again" : "Checking lock…"}
              </Button>
            )}
            <p
              role="status"
              aria-live="polite"
              className="mt-4 min-h-10 max-w-72 text-xs leading-relaxed text-muted-foreground"
            >
              {error || connectionError}
            </p>
          </div>
        </main>
      ) : error || connectionError ? (
        <div
          role="alert"
          className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-xl border border-border bg-background px-4 py-3 text-sm"
        >
          {error || connectionError}
        </div>
      ) : null}
    </Context.Provider>
  );
}
